import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import styles from "./HowItWorks.module.scss";

const steps = [
  {
    id: "sees",
    step: "01",
    title: "Sees everything",
    description:
      "Reads every email, meeting, CRM change, and signal across your stack — in the background.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M10 4C6 4 2.73 6.61 1 10C2.73 13.39 6 16 10 16C14 16 17.27 13.39 19 10C17.27 6.61 14 4 10 4Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: "knows",
    step: "02",
    title: "Knows what matters",
    description:
      "Separates real signal from noise across every lead, account, and deal.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M10 3C7.5 3 5.5 5 5.5 7.5C5.5 11 10 17 10 17C10 17 14.5 11 14.5 7.5C14.5 5 12.5 3 10 3Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="10" cy="7.5" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: "next",
    step: "03",
    title: "Tells you what's next",
    description:
      "Hands you one ranked plan for today, tomorrow, and the week ahead.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M6 5H14M6 10H14M6 15H11"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="4" cy="5" r="1" fill="currentColor" />
        <circle cx="4" cy="10" r="1" fill="currentColor" />
        <circle cx="4" cy="15" r="1" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: "work",
    step: "04",
    title: "Does the work with you",
    description:
      "Drafts the replies, preps the meetings, updates CRM, and creates the tasks.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M11 2L4 11H10L9 18L16 9H10L11 2Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: "learns",
    step: "05",
    title: "Learns how you sell",
    description:
      "Coaches in the moment and sharpens every next move it makes for you.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M3 8L10 4L17 8V14C17 14.55 16.55 15 16 15H4C3.45 15 3 14.55 3 14V8Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7 15V11H13V15"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
];

const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const [activeId, setActiveId] = useState("sees");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      id="how-it-works"
      ref={ref}
      className={styles.section}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container-custom">
        <motion.div className={styles.badge} variants={itemVariants}>
          <span className={styles.badgeDot} aria-hidden="true" />
          How It Works
        </motion.div>

        <motion.h2 className={styles.headline} variants={itemVariants}>
          It works the way a great Chief of Staff would
        </motion.h2>

        <motion.p className={styles.lead} variants={itemVariants}>
          Vertik doesn&apos;t wait for you to open a tool. It runs a continuous
          loop in the background — seeing, deciding, acting, and learning — so
          every rep starts the day already ahead.
        </motion.p>

        <motion.div className={styles.stepsGrid} variants={itemVariants} data-ui="steps-grid">
          {steps.map((step, index) => {
            const isActive = activeId === step.id;

            return (
              <motion.button
                key={step.id}
                type="button"
                className={`${styles.stepCard} ${isActive ? styles.stepCardActive : ""}`}
                data-ui="step-card"
                onClick={() => setActiveId(step.id)}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.99 }}
                aria-pressed={isActive}
              >
                <div className={styles.stepTop}>
                  <span className={styles.stepIcon}>{step.icon}</span>
                  <span className={styles.stepNumber}>{step.step}</span>
                </div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
                {index < steps.length - 1 && (
                  <span className={styles.stepDivider} aria-hidden="true" />
                )}
              </motion.button>
            );
          })}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default HowItWorks;
